import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { INDICATOR_DOMAINS } from '@/api/rest/resources/types/IndicatorProduct';
import { RecommendationProductCode } from '@/api/rest/resources/types/recommendations';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationOrgWidgetsByDomain } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { isNullish } from '@/utils/booleans';
import { formatFactCarbonToCO2, formatFactKilogramsToTonnes } from '@/utils/formatting';

import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useBGCarbonCells, useDefaultCustomCells, useOrderedCustomCells } from '../plots/hooks/useGetCustomCells';
import { ProjectTypeAccessorKey } from '../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { LandPageLayout } from './components/LandPageLayout';

const PAGE_WIDGET_NAME = 'default_template_asset_bgc';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const LandSoilPage = () => {
  const { t } = useTranslation();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );
  const widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;

  const defaultCells = [...useDefaultCustomCells(), ...useBGCarbonCells()];
  const orderedCustomCells = useOrderedCustomCells(defaultCells, [
    ProjectTypeAccessorKey.carbonStorageBgTrend_1y,
    ProjectTypeAccessorKey.carbonStorageBg,
  ]);

  return (
    <LandPageLayout
      signedInPageLayoutProps={{
        customizableWidgetTemplateProps: {
          widgetName: PAGE_WIDGET_NAME,
          widgetType: PAGE_WIDGET_TYPE,
          overrideLayoutConfiguration: defaultWidgetConfiguration,
        },
      }}
      navBarProps={{
        title: t('navbar.soil'),
      }}
      plotListProps={{
        customColumns: orderedCustomCells,
      }}
    >
      <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
    </LandPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  const { t } = useTranslation();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();
  const showSummaryCard = !isLimitedAccessUser && !isNullish(activeFilters) && activeFilters.length === 0;
  const inSituWidgets = useInSituIndicatorConfigurationOrgWidgetsByDomain(INDICATOR_DOMAINS.SOIL);

  const bgcHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage'),
    headerTooltip: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'bgc_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col lg:flex-row lg:flex-wrap items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_bgc,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.permanent_soil_carbon_per_ha,
              headerText: 'global.widget.currentBgc',
              tooltipText: 'global.widget.currentBgcTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_current_bgc_co2,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.permanent_soil_carbon_per_ha,
              headerText: 'global.widget.currentBgcCo2Equivalent',
              tooltipText: 'global.widget.currentBgcCo2EquivalentTooltip',
              formatter: formatFactCarbonToCO2,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_bgc_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.permanent_soil_carbon_per_ha_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgBgcUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_bgc_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha_pct_change,
              headerText: 'global.widget.projection1yBgcUplift',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'carbon_summary',
        type: WidgetType.o_recommendation_summary,
        visible: showSummaryCard,
        relatedData: {
          productCode: RecommendationProductCode.EK_CARBON,
        },
      },
      {
        name: 'asset_bgc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: bgcHeaderProps,
          graphTileType: GraphTileType.historic_below_ground_carbon,
        },
      },
      ...inSituWidgets,
    ],
  };
};
